/**
 * Org-side Templates Gallery — instantiate a curated form template into the org.
 * Opens from FormsListPage. Calls POST /api/templates/{id}/use.
 */
import { useEffect, useMemo, useState } from "react";
import { api, extractError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Stack, MagnifyingGlass, Sparkle, ArrowRight, FilesIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

export function TemplatesGalleryDialog({ open, onOpenChange, onUsed }) {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [activeCat, setActiveCat] = useState("ALL");
    const [picked, setPicked] = useState(null);  // template the admin clicked
    const [newName, setNewName] = useState("");
    const [newCode, setNewCode] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        api.get("/templates")
            .then((r) => setTemplates(r.data))
            .catch((e) => toast.error(extractError(e)))
            .finally(() => setLoading(false));
    }, [open]);

    const categories = useMemo(() => {
        const set = new Set(templates.map((t) => t.category || "General"));
        return ["ALL", ...Array.from(set).sort()];
    }, [templates]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return templates.filter((t) => {
            if (activeCat !== "ALL" && (t.category || "General") !== activeCat) return false;
            if (!q) return true;
            return [t.name, t.description, t.category].filter(Boolean).some((s) => s.toLowerCase().includes(q));
        });
    }, [templates, search, activeCat]);

    const pick = async (t) => {
        try {
            const { data } = await api.get(`/templates/${t.id}`);
            setPicked(data);
            setNewName(data.bundle?.form?.form_title || data.bundle?.form?.form_name || data.name);
            setNewCode(data.bundle?.form?.form_code || "");
        } catch (e) { toast.error(extractError(e)); }
    };

    const close = () => { onOpenChange(false); setTimeout(() => { setPicked(null); setNewName(""); setNewCode(""); setSearch(""); setActiveCat("ALL"); }, 200); };

    const use = async () => {
        if (!picked) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/templates/${picked.id}/use`, {
                new_form_name: newName || undefined,
                new_form_code: newCode || undefined,
            });
            toast.success(`Template imported as "${data.form_name}"`);
            onUsed?.(data);
            close();
        } catch (e) { toast.error(extractError(e)); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange(true); }}>
            <DialogContent className="rounded-sm max-w-4xl max-h-[88vh] overflow-hidden flex flex-col p-0" data-testid="templates-gallery">
                {!picked ? (
                    <>
                        <DialogHeader className="px-6 pt-6">
                            <DialogTitle className="flex items-center gap-2"><Sparkle size={18} weight="duotone" className="text-primary" /> Templates Gallery</DialogTitle>
                            <DialogDescription>Start with a ready-made form. Each template includes sections, fields, workflow steps and access rules.</DialogDescription>
                        </DialogHeader>
                        <div className="px-6 pb-3 border-b border-border flex flex-wrap gap-3 items-center" data-testid="templates-gallery-toolbar">
                            <div className="relative flex-1 min-w-[200px]">
                                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search templates…" className="pl-9 rounded-sm h-9" data-testid="gallery-search-input" />
                            </div>
                            <div className="flex flex-wrap gap-1" data-testid="gallery-categories">
                                {categories.map((c) => (
                                    <button
                                        key={c} type="button"
                                        onClick={() => setActiveCat(c)}
                                        className={`px-3 py-1 rounded-sm text-xs font-mono uppercase tracking-wide border transition-colors ${activeCat === c ? "bg-foreground text-background border-foreground" : "bg-card text-muted-foreground border-border hover:border-foreground/30"}`}
                                        data-testid={`gallery-cat-${c}`}
                                    >
                                        {c}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="overflow-y-auto p-6 flex-1">
                            {loading ? (
                                <div className="text-sm text-muted-foreground">Loading templates…</div>
                            ) : filtered.length === 0 ? (
                                <div className="text-center py-12">
                                    <Stack size={36} weight="duotone" className="mx-auto text-muted-foreground mb-3" />
                                    <div className="text-sm font-medium">No templates {search ? "match your search" : "available yet"}</div>
                                    <div className="text-xs text-muted-foreground mt-1">Ask your platform admin to publish a few.</div>
                                </div>
                            ) : (
                                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="gallery-grid">
                                    {filtered.map((t) => (
                                        <button
                                            key={t.id} type="button"
                                            onClick={() => pick(t)}
                                            className="brut-card p-5 text-left hover:border-primary/50 transition-colors"
                                            data-testid={`gallery-template-${t.id}`}
                                        >
                                            <div className="flex items-start justify-between mb-2">
                                                <Badge variant="outline" className="rounded-sm font-mono text-[9px]">{t.category}</Badge>
                                                <ArrowRight size={14} className="text-muted-foreground" />
                                            </div>
                                            <h3 className="text-base font-semibold leading-tight mb-1">{t.name}</h3>
                                            {t.description && <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{t.description}</p>}
                                            <div className="flex items-center gap-2 text-[10px] font-mono text-muted-foreground">
                                                <FilesIcon size={10} />
                                                <span>{t.fields_count} fields</span>
                                                {t.workflow_steps_count > 0 && <>· <span>{t.workflow_steps_count} steps</span></>}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <>
                        <DialogHeader className="px-6 pt-6">
                            <DialogTitle>{picked.name}</DialogTitle>
                            <DialogDescription>Confirm a name and code, then we'll create the form in your org.</DialogDescription>
                        </DialogHeader>
                        <div className="px-6 py-4 space-y-4 overflow-y-auto" data-testid="gallery-confirm">
                            <div className="border border-border rounded-sm p-3 bg-secondary/40 text-xs grid grid-cols-2 gap-1">
                                <div><span className="label-overline">Sections:</span> {picked.sections_count}</div>
                                <div><span className="label-overline">Fields:</span> {picked.fields_count}</div>
                                <div><span className="label-overline">Workflow steps:</span> {picked.workflow_steps_count}</div>
                                <div><span className="label-overline">Category:</span> {picked.category}</div>
                            </div>
                            {picked.description && <p className="text-sm text-muted-foreground">{picked.description}</p>}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="label-overline">New form name</Label>
                                    <Input value={newName} onChange={(e) => setNewName(e.target.value)} className="mt-2 rounded-sm" data-testid="gallery-new-name" />
                                </div>
                                <div>
                                    <Label className="label-overline">New form code</Label>
                                    <Input value={newCode} onChange={(e) => setNewCode(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))} className="mt-2 rounded-sm font-mono" data-testid="gallery-new-code" />
                                </div>
                            </div>
                            <p className="text-xs text-muted-foreground">
                                Access references (groups, roles) are matched by name in your org. Any unmatched ones will be skipped.
                            </p>
                        </div>
                        <DialogFooter className="px-6 py-4 border-t border-border">
                            <Button variant="ghost" onClick={() => setPicked(null)} className="rounded-sm">← Back</Button>
                            <Button onClick={use} disabled={busy} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="gallery-use-btn">
                                {busy ? "Creating…" : "Create form"}
                            </Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
